import { Component, ElementRef, input, output, viewChild } from '@angular/core';
import { AdminIconComponent } from '../../admin';

@Component({
  selector: 'app-lead-profile-avatar',
  standalone: true,
  imports: [AdminIconComponent],
  template: `
    <div class="lead-profile-avatar" [class.lead-profile-avatar--compact]="compact()">
      <div
        class="lead-profile-avatar__frame"
        [class.lead-profile-avatar__frame--busy]="uploading() || removing()"
      >
        @if (photoUrl()) {
          <img class="lead-profile-avatar__photo" [src]="photoUrl()" [alt]="fullName() + ' photo'" />
        } @else {
          <span class="lead-profile-avatar__initials" aria-hidden="true">{{ initials() }}</span>
        }
        @if (uploading() || removing()) {
          <span class="lead-profile-avatar__overlay" aria-live="polite">
            {{ uploading() ? 'Uploading…' : 'Removing…' }}
          </span>
        }
        @if (photoUrl() && showVerifiedBadge()) {
          <span class="lead-profile-avatar__verified" aria-label="Verified photo">
            <app-admin-icon name="badge-check" [size]="12" />
          </span>
        }
        <button
          type="button"
          class="lead-profile-avatar__edit"
          [disabled]="uploading() || removing()"
          [attr.aria-label]="photoUrl() ? 'Change passport photo' : 'Upload passport photo'"
          (click)="openFilePicker()"
        >
          <app-admin-icon name="pencil" [size]="14" />
        </button>
      </div>
      <input
        #fileInput
        type="file"
        class="lead-profile-avatar__file-input"
        accept="image/jpeg,image/png,image/webp,image/gif"
        tabindex="-1"
        aria-hidden="true"
        (change)="onFileChange($event)"
      />
      @if (showRemoveLink() && photoUrl() && !uploading() && !removing()) {
        <button type="button" class="lead-profile-avatar__remove" (click)="removePhoto.emit()">
          Remove photo
        </button>
      }
      @if (hint()) {
        <p class="lead-profile-avatar__hint">{{ hint() }}</p>
      }
    </div>
  `,
  styles: `
    .lead-profile-avatar {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.35rem;
    }
    .lead-profile-avatar--compact {
      align-items: flex-start;
    }
    .lead-profile-avatar__frame {
      position: relative;
      width: 7.5rem;
      aspect-ratio: 3 / 4;
      border-radius: 0.5rem;
      border: 1px solid #e2e8f0;
      background: linear-gradient(145deg, #eef2ff 0%, #f8fafc 55%, #e2e8f0 100%);
      overflow: hidden;
      flex-shrink: 0;
    }
    .lead-profile-avatar--compact .lead-profile-avatar__frame {
      width: 6.5rem;
    }
    .lead-profile-avatar__frame--busy {
      opacity: 0.85;
    }
    .lead-profile-avatar__photo {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
    .lead-profile-avatar__initials {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      font-size: 1.65rem;
      font-weight: 700;
      letter-spacing: 0.04em;
      color: #334155;
    }
    .lead-profile-avatar__overlay {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(15, 23, 42, 0.45);
      color: #fff;
      font-size: 0.72rem;
      font-weight: 600;
      text-align: center;
      padding: 0.5rem;
    }
    .lead-profile-avatar__verified {
      position: absolute;
      right: 0.35rem;
      bottom: 0.35rem;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.25rem;
      height: 1.25rem;
      border-radius: 999px;
      background: #22c55e;
      color: #fff;
      border: 2px solid #fff;
      box-shadow: 0 1px 3px rgba(15, 23, 42, 0.15);
    }
    .lead-profile-avatar__edit {
      position: absolute;
      right: 0.35rem;
      top: 0.35rem;
      width: 1.75rem;
      height: 1.75rem;
      border: none;
      border-radius: 999px;
      background: #fff;
      color: #0f172a;
      box-shadow: 0 1px 4px rgba(15, 23, 42, 0.18);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: background 0.15s ease, transform 0.15s ease;
    }
    .lead-profile-avatar__edit:hover:not(:disabled) {
      background: #f1f5f9;
      transform: scale(1.05);
    }
    .lead-profile-avatar__edit:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    .lead-profile-avatar__file-input {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }
    .lead-profile-avatar__remove {
      border: none;
      background: none;
      padding: 0;
      font-size: 0.72rem;
      color: #64748b;
      text-decoration: underline;
      cursor: pointer;
    }
    .lead-profile-avatar__remove:hover {
      color: #b91c1c;
    }
    .lead-profile-avatar__hint {
      margin: 0;
      font-size: 0.72rem;
      color: #94a3b8;
      text-align: center;
      max-width: 10rem;
      line-height: 1.35;
    }
  `,
})
export class LeadProfileAvatarComponent {
  readonly photoUrl = input<string | undefined>();
  readonly fullName = input('');
  readonly uploading = input(false);
  readonly removing = input(false);
  readonly hint = input<string | undefined>();
  readonly showRemoveLink = input(true);
  readonly showVerifiedBadge = input(false);
  readonly compact = input(false);

  readonly photoSelected = output<File>();
  readonly removePhoto = output<void>();

  private readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  initials(): string {
    const name = this.fullName().trim();
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase();
    }
    const single = parts[0] ?? '?';
    return single.slice(0, 2).toUpperCase();
  }

  openFilePicker(): void {
    this.fileInput()?.nativeElement.click();
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (file) {
      this.photoSelected.emit(file);
    }
  }
}
